import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
    Plus,
    Search,
    Mic,
    RefreshCw,
    FileText,
    Printer,
    Download,
    Eye,
    CreditCard,
    X,
    Save,
    IndianRupee,
    Package,
    Truck,
    Wallet,
    CalendarDays,
    AlertCircle,
    CheckCircle2,
    Pencil,
    Trash2
} from "lucide-react";
import "./CustomerCharges.css";

const API_URL =
    process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
    baseURL: API_URL,
    timeout: 20000
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("erp_token");

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

const today = () => {
    const date = new Date();

    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0")
    ].join("-");
};

const emptyForm = () => ({
    grnId: "",
    billDate: today(),
    transportBillNo: "",
    transportBillAmount: "",
    interestRateMonthly: "2",
    hamaliAmount: "",
    storageRentAmount: "",
    otherCharges: "",
    discountAmount: "",
    notes: ""
});

const emptyPayment = () => ({
    paymentDate: today(),
    paymentMethod: "Cash",
    amount: "",
    referenceNo: "",
    notes: ""
});

const number = (value) => {
    const result = Number(value ?? 0);
    return Number.isFinite(result) ? result : 0;
};

const currency = (value) =>
    number(value).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const formatDate = (value) => {
    if (!value) return "—";

    const date = String(value).slice(0, 10);
    const parts = date.split("-");

    if (parts.length !== 3) return date;

    return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (char) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;"
    })[char]);

const getErrorMessage = (error, fallback) =>
    error.response?.data?.message || error.message || fallback;

function CustomerCharges() {
    const [grns, setGrns] = useState([]);
    const [bills, setBills] = useState([]);
    const [selectedGrn, setSelectedGrn] = useState(null);

    const [form, setForm] = useState(emptyForm);
    const [paymentForm, setPaymentForm] = useState(emptyPayment);
    const [grnSearch, setGrnSearch] = useState("");
    const [isListening, setIsListening] = useState(false);
    const [voiceError, setVoiceError] = useState("");

    const [showForm, setShowForm] = useState(false);
    const [editingBillId, setEditingBillId] = useState(null);
    const [paymentBill, setPaymentBill] = useState(null);
    const [viewBill, setViewBill] = useState(null);

    const [search, setSearch] = useState("");

    const [registerListening, setRegisterListening] = useState(false);

    const startRegisterVoiceSearch = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            window.alert("Voice search requires an updated Chrome browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = "mr-IN";
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onresult = (event) => {
            const spokenText = event.results?.[0]?.[0]?.transcript || "";
            if (spokenText.trim()) setSearch(spokenText.trim());
        };
        recognition.onerror = (event) => {
            if (event.error === "not-allowed") {
                window.alert("Please allow microphone access in your browser.");
            } else if (event.error === "no-speech") {
                window.alert("No speech detected. Please try again.");
            }
        };
        recognition.onend = () => setRegisterListening(false);

        try {
            setRegisterListening(true);
            recognition.start();
        } catch (error) {
            setRegisterListening(false);
            console.error("Voice search failed:", error);
        }
    };

    const [statusFilter, setStatusFilter] = useState("ALL");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingPayment, setSavingPayment] = useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const normalizeSearch = (value) =>
        String(value ?? "")
            .normalize("NFKC")
            .replace(/\s+/g, " ")
            .trim()
            .toLocaleLowerCase();

    const filteredGrns = grns.filter((grn) => {
        const query = normalizeSearch(grnSearch);

        if (!query) return true;

        const grnNo = normalizeSearch(grn.grn_no);
        const customerName = normalizeSearch(grn.customer_name);
        const grnDate = normalizeSearch(formatDate(grn.grn_date));
        const fullLabel = normalizeSearch(
            `${grn.grn_no ?? ""} — ${grn.customer_name ?? ""} — ${formatDate(grn.grn_date)}`
        );

        return (
            grnNo.includes(query) ||
            customerName.includes(query) ||
            grnDate.includes(query) ||
            fullLabel.includes(query)
        );
    });

    const startMarathiVoiceSearch = () => {
        setVoiceError("");

        const SpeechRecognition =
            window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            setVoiceError(
                "Voice typing is not supported in this browser. Please use Chrome."
            );
            return;
        }

        const recognition = new SpeechRecognition();

        recognition.lang = "mr-IN";
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
            setIsListening(true);
        };

        recognition.onresult = (event) => {
            const spokenText = event.results?.[0]?.[0]?.transcript || "";

            setGrnSearch(spokenText.trim());
            if (form.grnId) {
                updateForm("grnId", "");
                setSelectedGrn(null);
            }
            setVoiceError("");
        };

        recognition.onerror = (event) => {
            if (event.error === "not-allowed") {
                setVoiceError(
                    "Please allow microphone access in your browser settings."
                );
            } else if (event.error === "no-speech") {
                setVoiceError("No speech detected. Please try speaking again.");
            } else {
                setVoiceError("Voice typing failed. Please try again.");
            }
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        recognition.start();
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const [grnResponse, billResponse] = await Promise.all([
                api.get("/customer-charges/grns"),
                api.get("/customer-charges/bills")
            ]);

            setGrns(grnResponse.data?.data || []);
            setBills(billResponse.data?.data || []);
        } catch (err) {
            console.error("Customer charges load error:", err);

            setError(
                getErrorMessage(
                    err,
                    "Unable to load customer bills. Check the backend server."
                )
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const loadSelectedGrn = async (grnId) => {
        setSelectedGrn(null);

        if (!grnId) return;

        try {
            const response = await api.get(
                `/customer-charges/grns/${grnId}`
            );

            const grn = response.data?.data;
            setSelectedGrn(grn || null);

            if (grn?.grn_date) {
                setForm((previous) => ({
                    ...previous,
                    grnId: String(grnId),
                    billDate:
                        previous.billDate <
                        String(grn.grn_date).slice(0, 10)
                            ? String(grn.grn_date).slice(0, 10)
                            : previous.billDate
                }));
            }
        } catch (err) {
            setError(
                getErrorMessage(err, "Unable to load GRN details.")
            );
        }
    };

    const updateForm = (field, value) => {
        setForm((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const updatePaymentForm = (field, value) => {
        setPaymentForm((previous) => ({
            ...previous,
            [field]: value
        }));
    };

    const interestDays = useMemo(() => {
        if (!selectedGrn?.grn_date || !form.billDate) return 0;

        const start = Date.parse(
            `${String(selectedGrn.grn_date).slice(0, 10)}T00:00:00Z`
        );

        const end = Date.parse(`${form.billDate}T00:00:00Z`);

        if (
            !Number.isFinite(start) ||
            !Number.isFinite(end) ||
            end < start
        ) {
            return 0;
        }

        return Math.floor((end - start) / 86400000);
    }, [selectedGrn, form.billDate]);

    const calculation = useMemo(() => {
        const transport = Math.max(
            0,
            number(form.transportBillAmount)
        );

        const rate = Math.max(
            0,
            number(form.interestRateMonthly)
        );

        const interest =
            Math.round(
                (
                    transport * rate * interestDays / 3000 +
                    Number.EPSILON
                ) * 100
            ) / 100;

        const hamali = Math.max(0, number(form.hamaliAmount));
        const rent = Math.max(0, number(form.storageRentAmount));
        const other = Math.max(0, number(form.otherCharges));
        const discount = Math.max(0, number(form.discountAmount));

        const total = Math.max(
            0,
            Math.round(
                (
                    transport +
                    interest +
                    hamali +
                    rent +
                    other -
                    discount +
                    Number.EPSILON
                ) * 100
            ) / 100
        );

        return {
            transport,
            interest,
            hamali,
            rent,
            other,
            discount,
            total
        };
    }, [form, interestDays]);

    const openCreateForm = () => {
        setEditingBillId(null);
        setForm(emptyForm());
        setSelectedGrn(null);
        setShowForm(true);
        setError("");
        setSuccess("");
    };

    const closeCreateForm = () => {
        if (saving) return;

        setShowForm(false);
        setSelectedGrn(null);
        setEditingBillId(null);
        setForm(emptyForm());
    };

    const createBill = async (event) => {
        event.preventDefault();

        setError("");
        setSuccess("");

        if (!form.grnId) {
            setError("Please select a Goods Received record.");
            return;
        }

        if (!selectedGrn) {
            setError("Wait for the selected GRN details to load.");
            return;
        }

        if (
            form.billDate <
            String(selectedGrn.grn_date).slice(0, 10)
        ) {
            setError(
                "Bill date cannot be earlier than the Goods Received date."
            );
            return;
        }

        if (calculation.total <= 0) {
            setError(
                "The final bill amount must be greater than zero."
            );
            return;
        }

        setSaving(true);

        const payload = {
            grnId: Number(form.grnId),
            billDate: form.billDate,
            transportBillNo: form.transportBillNo.trim(),
            transportBillAmount: calculation.transport,
            interestRateMonthly: number(form.interestRateMonthly),
            hamaliAmount: calculation.hamali,
            storageRentAmount: calculation.rent,
            otherCharges: calculation.other,
            discountAmount: calculation.discount,
            notes: form.notes.trim()
        };

        try {
            let response;

            if (editingBillId) {
                response = await api.put(
                    `/customer-charges/bills/${editingBillId}`,
                    payload
                );
            } else {
                response = await api.post(
                    "/customer-charges/bills",
                    payload
                );
            }

            const savedBillId =
                response.data?.data?.id || editingBillId;

            const wasEditing = Boolean(editingBillId);

            setShowForm(false);
            setSelectedGrn(null);
            setEditingBillId(null);
            setForm(emptyForm());

            setSuccess(
                wasEditing
                    ? "Customer bill updated successfully."
                    : `Bill ${
                          response.data?.data?.billNo || ""
                      } created successfully.`
            );

            await loadData();

            if (savedBillId) {
                await openBill(savedBillId);
            }
        } catch (err) {
            setError(
                getErrorMessage(
                    err,
                    editingBillId
                        ? "Unable to update customer bill."
                        : "Unable to create customer bill."
                )
            );
        } finally {
            setSaving(false);
        }
    };

    const openBill = async (billId) => {
        setError("");

        try {
            const response = await api.get(
                `/customer-charges/bills/${billId}`
            );

            setViewBill(response.data?.data || null);
        } catch (err) {
            setError(
                getErrorMessage(err, "Unable to open customer bill.")
            );
        }
    };

    const editBill = async (billId) => {
        setError("");
        setSuccess("");

        try {
            const response = await api.get(
                `/customer-charges/bills/${billId}`
            );

            const bill = response.data?.data;

            if (!bill) {
                setError("Unable to find the selected bill.");
                return;
            }

            const grnId = bill.grn_id ?? bill.grnId;

            if (!grnId) {
                setError(
                    "The GRN linked to this bill could not be found."
                );
                return;
            }

            const grnResponse = await api.get(
                `/customer-charges/grns/${grnId}`
            );

            const grn = grnResponse.data?.data;

            if (!grn) {
                setError("Unable to load the linked GRN details.");
                return;
            }

            setSelectedGrn(grn);

            setForm({
                grnId: String(grnId),
                billDate: String(bill.bill_date).slice(0, 10),
                transportBillNo: bill.transport_bill_no || "",
                transportBillAmount: String(
                    number(bill.transport_bill_amount)
                ),
                interestRateMonthly: String(
                    number(bill.interest_rate_monthly)
                ),
                hamaliAmount: String(number(bill.hamali_amount)),
                storageRentAmount: String(
                    number(bill.storage_rent_amount)
                ),
                otherCharges: String(number(bill.other_charges)),
                discountAmount: String(number(bill.discount_amount)),
                notes: bill.notes || ""
            });

            setEditingBillId(bill.id);
            setViewBill(null);
            setPaymentBill(null);
            setShowForm(true);
        } catch (err) {
            setError(
                getErrorMessage(err, "Unable to load bill for editing.")
            );
        }
    };

    const deleteBill = async (bill) => {
        if (number(bill.amount_paid) > 0) {
            setError(
                "This bill has recorded payments and cannot be deleted."
            );
            return;
        }

        const confirmed = window.confirm(
            `Delete bill ${bill.bill_no} for ${bill.customer_name}? This action cannot be undone.`
        );

        if (!confirmed) return;

        setError("");
        setSuccess("");

        try {
            await api.delete(
                `/customer-charges/bills/${bill.id}`
            );

            if (viewBill?.id === bill.id) {
                setViewBill(null);
            }

            setSuccess(
                `Bill ${bill.bill_no} deleted successfully.`
            );

            await loadData();
        } catch (err) {
            setError(
                getErrorMessage(err, "Unable to delete customer bill.")
            );
        }
    };

    const submitPayment = async (event) => {
        event.preventDefault();

        if (!paymentBill) return;

        const amount = number(paymentForm.amount);
        const balance = number(paymentBill.balance_due);

        if (amount <= 0) {
            setError("Enter a payment amount greater than zero.");
            return;
        }

        if (amount > balance + 0.001) {
            setError(`Payment cannot exceed ${currency(balance)}.`);
            return;
        }

        setSavingPayment(true);
        setError("");

        try {
            await api.post(
                `/customer-charges/bills/${paymentBill.id}/payments`,
                {
                    paymentDate: paymentForm.paymentDate,
                    paymentMethod: paymentForm.paymentMethod,
                    amount,
                    referenceNo: paymentForm.referenceNo.trim(),
                    notes: paymentForm.notes.trim()
                }
            );

            setPaymentBill(null);
            setPaymentForm(emptyPayment());
            setSuccess("Payment recorded successfully.");

            await loadData();

            if (viewBill?.id === paymentBill.id) {
                await openBill(paymentBill.id);
            }
        } catch (err) {
            setError(
                getErrorMessage(err, "Unable to record payment.")
            );
        } finally {
            setSavingPayment(false);
        }
    };

    const filteredBills = useMemo(() => {
        const query = search.trim().toLowerCase();

        return bills.filter((bill) => {
            const matchesSearch = !query || [
                bill.bill_no,
                bill.grn_no,
                bill.customer_name,
                bill.transport_bill_no
            ].some((value) =>
                String(value ?? "").toLowerCase().includes(query)
            );

            const status = bill.payment_status || "Unpaid";

            const matchesStatus =
                statusFilter === "ALL" ||
                status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [bills, search, statusFilter]);

    const summary = useMemo(() => ({
        count: bills.length,
        total: bills.reduce(
            (sum, bill) => sum + number(bill.total_amount),
            0
        ),
        paid: bills.reduce(
            (sum, bill) => sum + number(bill.amount_paid),
            0
        ),
        balance: bills.reduce(
            (sum, bill) => sum + number(bill.balance_due),
            0
        )
    }), [bills]);

    const clearMessages = () => {
        setError("");
        setSuccess("");
    };

        const exportExcel = () => {
        const summaryRows = filteredBills.map((bill) => ({
            "Bill No.": bill.bill_no,
            "Bill Date": formatDate(bill.bill_date),
            "GRN No.": bill.grn_no,
            "Goods Received Date": formatDate(bill.grn_date),
            "Customer": bill.customer_name,
            "Transport Bill No.": bill.transport_bill_no || "",
            "Transport Bill Amount": number(bill.transport_bill_amount),
            "Monthly Interest Rate (%)": number(bill.interest_rate_monthly),
            "Interest Days": number(bill.interest_days),
            "Transport Interest": number(bill.transport_interest_amount),
            "Hamali": number(bill.hamali_amount),
            "Storage Rent": number(bill.storage_rent_amount),
            "Other Charges": number(bill.other_charges),
            "Discount": number(bill.discount_amount),
            "Total Amount": number(bill.total_amount),
            "Amount Paid": number(bill.amount_paid),
            "Balance Due": number(bill.balance_due),
            "Status": bill.payment_status || "Unpaid",
            "Notes": bill.notes || ""
        }));

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(summaryRows),
            "Customer Bills"
        );

        const saveFile = async () => {
            try {
                const details = [];
                const paymentDetails = [];

                for (const bill of filteredBills) {
                    try {
                        const response = await api.get(
                            `/customer-charges/bills/${bill.id}`
                        );

                        const detail = response.data?.data;

                        if (!detail) continue;

                        (detail.items || []).forEach((item) => {
                            details.push({
                                "Bill No.": detail.bill_no,
                                "Bill Date": formatDate(detail.bill_date),
                                "Customer": detail.customer_name,
                                "GRN No.": detail.grn_no,
                                "Chamber No.": item.chamber_no || "",
                                "Lot No.": item.lot_no || "",
                                "Product Mark": item.product_mark || "",
                                "Location": item.location || "",
                                "Bags": number(item.bags),
                                "Weight (KG)": number(item.weight_kg)
                            });
                        });

                        (detail.payments || []).forEach((payment) => {
                            paymentDetails.push({
                                "Bill No.": detail.bill_no,
                                "Customer": detail.customer_name,
                                "Payment Date": formatDate(payment.payment_date),
                                "Payment Method": payment.payment_method,
                                "Amount": number(payment.amount),
                                "Reference No.": payment.reference_no || "",
                                "Notes": payment.notes || ""
                            });
                        });
                    } catch (detailError) {
                        console.error(
                            `Unable to export details for ${bill.bill_no}:`,
                            detailError
                        );
                    }
                }

                XLSX.utils.book_append_sheet(
                    workbook,
                    XLSX.utils.json_to_sheet(details),
                    "GRN Lot Details"
                );

                XLSX.utils.book_append_sheet(
                    workbook,
                    XLSX.utils.json_to_sheet(paymentDetails),
                    "Payment History"
                );

                XLSX.writeFile(
                    workbook,
                    `Customer_Bills_${today()}.xlsx`
                );
            } catch (err) {
                setError("Unable to export Excel file.");
                console.error("Excel export error:", err);
            }
        };

        if (!filteredBills.length) {
            XLSX.writeFile(
                workbook,
                `Customer_Bills_${today()}.xlsx`
            );
        } else {
            void saveFile();
        }
    };

    const printBill = async (billId) => {
        let bill = viewBill?.id === billId ? viewBill : null;

        if (!bill) {
            try {
                const response = await api.get(
                    `/customer-charges/bills/${billId}`
                );

                bill = response.data?.data;
            } catch (err) {
                setError(
                    getErrorMessage(
                        err,
                        "Unable to load bill for printing."
                    )
                );
                return;
            }
        }

        if (!bill) return;

        const items = bill.items || [];
        const payments = bill.payments || [];

        const marathiCurrency = (value) =>
            `₹ ${number(value).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;

        const rows = items.map((item, index) => `
            <tr>
                <td>${index + 1}</td>
                <td>${escapeHtml(item.chamber_no || "—")}</td>
                <td>${escapeHtml(item.lot_no || "—")}</td>
                <td>${escapeHtml(item.product_mark || "—")}</td>
                <td>${number(item.bags).toLocaleString("en-IN")}</td>
                <td>${number(item.weight_kg).toLocaleString("en-IN")}</td>
            </tr>
        `).join("");

        const paymentRows = payments.map((payment) => `
            <tr>
                <td>${escapeHtml(formatDate(payment.payment_date))}</td>
                <td>${escapeHtml(payment.payment_method || "—")}</td>
                <td>${escapeHtml(payment.reference_no || "—")}</td>
                <td class="num">${marathiCurrency(payment.amount)}</td>
            </tr>
        `).join("");

        const total = number(bill.total_amount);
        const paid = number(bill.amount_paid);
        const balance = number(bill.balance_due);

        const documentHtml = `
            <!DOCTYPE html>
            <html lang="mr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>ग्राहक बिल - ${escapeHtml(bill.bill_no)}</title>

                <link
                    href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap"
                    rel="stylesheet"
                >

                <style>
                    * {
                        box-sizing: border-box;
                    }

                    body {
                        margin: 0;
                        padding: 20px;
                        font-family: "Noto Sans Devanagari",
                                     "Nirmala UI", "Mangal", Arial, sans-serif;
                        font-size: 14px;
                        line-height: 1.6;
                        color: #202020;
                        background: #fff;
                    }

                    .sheet {
                        max-width: 950px;
                        margin: 0 auto;
                    }

                    .company {
                        text-align: center;
                        border: 1px solid #333;
                        padding: 14px 10px;
                    }

                    .company h1 {
                        font-size: 21px;
                        margin: 0 0 5px;
                        font-weight: 700;
                    }

                    .company p {
                        margin: 3px 0;
                    }

                    .bill-title {
                        margin: 10px auto 0;
                        display: inline-block;
                        padding: 4px 25px;
                        border: 1px solid #333;
                        font-size: 18px;
                        font-weight: 700;
                    }

                    .meta {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 8px 20px;
                        border: 1px solid #333;
                        border-top: 0;
                        padding: 12px;
                    }

                    .meta div {
                        overflow-wrap: anywhere;
                    }

                    h2 {
                        margin: 18px 0 7px;
                        font-size: 16px;
                    }

                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 7px;
                    }

                    th, td {
                        border: 1px solid #555;
                        padding: 7px 6px;
                        text-align: left;
                        vertical-align: middle;
                        overflow-wrap: anywhere;
                    }

                    th {
                        background: #f0f0f0;
                        font-weight: 700;
                    }

                    .num {
                        text-align: right;
                        white-space: nowrap;
                    }

                    .totals {
                        width: 65%;
                        margin-left: auto;
                    }

                    .grand {
                        font-weight: 700;
                        background: #f0f0f0;
                    }

                    .signatures {
                        display: flex;
                        justify-content: space-between;
                        gap: 30px;
                        margin-top: 65px;
                    }

                    .signatures div {
                        width: 220px;
                        padding-top: 8px;
                        border-top: 1px solid #333;
                        text-align: center;
                    }

                    .notes {
                        margin-top: 15px;
                        overflow-wrap: anywhere;
                    }

                    @page {
                        size: A4 portrait;
                        margin: 10mm;
                    }

                    @media print {
                        html,
                        body {
                            width: 100%;
                            margin: 0;
                            padding: 0;
                        }

                        body {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }

                        .sheet {
                            width: 100%;
                            max-width: none;
                            margin: 0;
                        }

                        .company,
                        .meta,
                        table {
                            width: 100%;
                        }

                        .totals {
                            width: 65%;
                            margin-left: auto;
                        }

                        thead {
                            display: table-header-group;
                        }

                        tr {
                            break-inside: avoid;
                            page-break-inside: avoid;
                        }
                    }

                    @media (max-width: 600px) {
                        body {
                            padding: 5px;
                            font-size: 12px;
                        }

                        .company h1 {
                            font-size: 17px;
                        }

                        .meta {
                            grid-template-columns: 1fr;
                        }

                        th, td {
                            padding: 4px;
                        }

                        .totals {
                            width: 100%;
                        }
                    }
                </style>
            </head>

            <body>
                <main class="sheet">
                    <header class="company">
                        <h1>
                            Bhimashankar Agro Coldchain &amp; Processing Producer Co. Ltd.
                        </h1>

                        <p>परगाव तर्फे खेड, गट नं. ६२८</p>

                        <div class="bill-title">
                            ग्राहक बिल / आकारणी पावती
                        </div>
                    </header>

                    <section class="meta">
                        <div>
                            <strong>बिल क्रमांक:</strong>
                            ${escapeHtml(bill.bill_no)}
                        </div>

                        <div>
                            <strong>बिल दिनांक:</strong>
                            ${escapeHtml(formatDate(bill.bill_date))}
                        </div>

                        <div>
                            <strong>ग्राहकाचे नाव:</strong>
                            ${escapeHtml(bill.customer_name)}
                        </div>

                        <div>
                            <strong>आवक क्रमांक:</strong>
                            ${escapeHtml(bill.grn_no)}
                        </div>

                        <div>
                            <strong>माल प्राप्त दिनांक:</strong>
                            ${escapeHtml(formatDate(bill.grn_date))}
                        </div>

                        <div>
                            <strong>वाहतूक बिल क्रमांक:</strong>
                            ${escapeHtml(bill.transport_bill_no || "—")}
                        </div>

                        <div>
                            <strong>गाडी क्रमांक:</strong>
                            ${escapeHtml(bill.vehicle_no || bill.grn_vehicle_no || "—")}
                        </div>
                    </section>

                    <h2>माल प्राप्त तपशील</h2>

                    <table>
                        <thead>
                            <tr>
                                <th>अ. क्र.</th>
                                <th>चेंबर नं.</th>
                                <th>लॉट नं.</th>
                                <th>मालाचा मार्क</th>
                                <th>बॅग</th>
                                <th>वजन (कि.ग्रॅ.)</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rows || "<tr><td colspan='6'>मालाचा तपशील उपलब्ध नाही</td></tr>"}
                        </tbody>
                    </table>

                    <h2>आकारणीचा तपशील</h2>

                    <table class="totals">
                        <tbody>
                            <tr>
                                <td>वाहतूक बिल</td>
                                <td class="num">${marathiCurrency(bill.transport_bill_amount)}</td>
                            </tr>

                            <tr>
                                <td>
                                    वाहतूक व्याज
                                    (${number(bill.interest_rate_monthly)}% प्रति महिना,
                                    ${number(bill.interest_days)} दिवस)
                                </td>
                                <td class="num">${marathiCurrency(bill.transport_interest_amount)}</td>
                            </tr>

                            <tr>
                                <td>हमाली / मजुरी</td>
                                <td class="num">${marathiCurrency(bill.hamali_amount)}</td>
                            </tr>

                            <tr>
                                <td>साठवण भाडे</td>
                                <td class="num">${marathiCurrency(bill.storage_rent_amount)}</td>
                            </tr>

                            <tr>
                                <td>इतर खर्च</td>
                                <td class="num">${marathiCurrency(bill.other_charges)}</td>
                            </tr>

                            <tr>
                                <td>सूट / वजावट</td>
                                <td class="num">− ${marathiCurrency(bill.discount_amount)}</td>
                            </tr>

                            <tr class="grand">
                                <td>एकूण देय रक्कम</td>
                                <td class="num">${marathiCurrency(total)}</td>
                            </tr>

                            <tr>
                                <td>जमा रक्कम</td>
                                <td class="num">${marathiCurrency(paid)}</td>
                            </tr>

                            <tr class="grand">
                                <td>शिल्लक रक्कम</td>
                                <td class="num">${marathiCurrency(balance)}</td>
                            </tr>
                        </tbody>
                    </table>

                    ${bill.notes ? `
                        <p class="notes">
                            <strong>शेरा:</strong>
                            ${escapeHtml(bill.notes)}
                        </p>
                    ` : ""}

                    <h2>जमा रकमेचा तपशील</h2>

                    <table>
                        <thead>
                            <tr>
                                <th>दिनांक</th>
                                <th>पेमेंट पद्धत</th>
                                <th>संदर्भ क्रमांक</th>
                                <th>जमा रक्कम</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${paymentRows || "<tr><td colspan='4'>पेमेंट नोंद उपलब्ध नाही</td></tr>"}
                        </tbody>
                    </table>

                    <div class="signatures">
                        <div>ग्राहकाची सही</div>
                        <div>अधिकृत सही व शिक्का</div>
                    </div>
                </main>

                <script>
                    window.onload = function () {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `;

        const printWindow = window.open(
            "",
            "_blank",
            "width=1000,height=800"
        );

        if (!printWindow) {
            setError(
                "Your browser blocked the print window. Allow pop-ups and try again."
            );
            return;
        }

        printWindow.document.open();
        printWindow.document.write(documentHtml);
        printWindow.document.close();
    };

    
        return (
        <div className="customer-charges">
            <header className="cc-page-header">
                <div className="cc-heading">
                    <div className="cc-heading-icon">
                        <FileText size={23} />
                    </div>

                    <div>
                        <h1>Customer Bills</h1>
                        <p>
                            Transport, interest, hamali, storage rent and payments
                        </p>
                    </div>
                </div>

                <div className="cc-header-actions">
                    <button
                        type="button"
                        className="cc-btn cc-btn-export"
                        onClick={exportExcel}
                        disabled={loading || bills.length === 0}
                    >
                        <Download size={17} />
                        Export Excel
                    </button>

                    <button
                        type="button"
                        className="cc-btn cc-btn-secondary"
                        onClick={loadData}
                        disabled={loading}
                    >
                        <RefreshCw
                            size={17}
                            className={loading ? "cc-spin" : ""}
                        />
                        Refresh
                    </button>

                    <button
                        type="button"
                        className="cc-btn cc-btn-primary"
                        onClick={openCreateForm}
                    >
                        <Plus size={18} />
                        Create Bill
                    </button>
                </div>
            </header>

            {error && (
                <div className="cc-alert cc-alert-error">
                    <AlertCircle size={19} />

                    <span>{error}</span>

                    <button
                        type="button"
                        onClick={clearMessages}
                        aria-label="Dismiss error"
                    >
                        <X size={17} />
                    </button>
                </div>
            )}

            {success && (
                <div className="cc-alert cc-alert-success">
                    <CheckCircle2 size={19} />

                    <span>{success}</span>

                    <button
                        type="button"
                        onClick={clearMessages}
                        aria-label="Dismiss success"
                    >
                        <X size={17} />
                    </button>
                </div>
            )}

            <section className="cc-stats">
                <div className="cc-stat-card">
                    <div className="cc-stat-icon cc-blue">
                        <FileText size={21} />
                    </div>

                    <div>
                        <span>Total Bills</span>
                        <strong>
                            {summary.count.toLocaleString("en-IN")}
                        </strong>
                    </div>
                </div>

                <div className="cc-stat-card">
                    <div className="cc-stat-icon cc-purple">
                        <IndianRupee size={21} />
                    </div>

                    <div>
                        <span>Total Receivable</span>
                        <strong>{currency(summary.total)}</strong>
                    </div>
                </div>

                <div className="cc-stat-card">
                    <div className="cc-stat-icon cc-green">
                        <Wallet size={21} />
                    </div>

                    <div>
                        <span>Amount Received</span>
                        <strong>{currency(summary.paid)}</strong>
                    </div>
                </div>

                <div className="cc-stat-card">
                    <div className="cc-stat-icon cc-orange">
                        <CreditCard size={21} />
                    </div>

                    <div>
                        <span>Balance Due</span>
                        <strong>{currency(summary.balance)}</strong>
                    </div>
                </div>
            </section>

            {showForm && (
                <section className="cc-panel cc-create-panel">
                    <div className="cc-panel-header">
                        <div>
                            <h2>
                                {editingBillId ? (
                                    <Pencil size={20} />
                                ) : (
                                    <Plus size={20} />
                                )}

                                {editingBillId
                                    ? "Edit Customer Bill"
                                    : "Create Customer Bill"}
                            </h2>

                            <p>
                                {editingBillId
                                    ? "Update charges for this bill. The linked GRN cannot be changed."
                                    : "Select a Goods Received record to reuse its customer and lot details."}
                            </p>
                        </div>

                        <button
                            type="button"
                            className="cc-icon-btn"
                            onClick={closeCreateForm}
                            aria-label="Close bill form"
                            disabled={saving}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <form onSubmit={createBill}>
                        <div className="cc-form-grid">
                            <div className="cc-field cc-field-wide">
                                <label>Goods Received / GRN *</label>

                                <div className="cc-grn-search">
                                    <div className="cc-grn-search-input">
                                        <Search size={19} aria-hidden="true" />

                                        <input
                                            type="text"
                                            value={grnSearch}
                                            onChange={(event) => {
                                                setGrnSearch(event.target.value);
                                                updateForm("grnId", "");
                                                setSelectedGrn(null);
                                            }}
                                            placeholder="Search customer name or GRN number..."
                                            aria-label="Search customer or GRN"
                                            disabled={Boolean(editingBillId) || saving}
                                        />

                                        <button
                                            type="button"
                                            className={`cc-mic-btn ${isListening ? "listening" : ""}`}
                                            onClick={startMarathiVoiceSearch}
                                            title="Speak customer name in Marathi"
                                            aria-label="Search using Marathi voice"
                                            disabled={Boolean(editingBillId) || saving}
                                        >
                                            <Mic size={20} />
                                        </button>
                                    </div>
                                </div>

                                {isListening && (
                                    <p className="cc-voice-status" role="status">
                                        Listening... ग्राहकाचे नाव मराठीत बोला.
                                    </p>
                                )}

                                {voiceError && (
                                    <p className="cc-voice-error" role="alert">
                                        {voiceError}
                                    </p>
                                )}

                                {!editingBillId && grnSearch.trim() && (
                                    <div className="cc-grn-results" role="listbox" aria-label="Matching customers and GRNs">
                                        {filteredGrns.length > 0 ? (
                                            filteredGrns.map((grn) => {
                                                const isSelected =
                                                    String(form.grnId) === String(grn.id);

                                                return (
                                                    <button
                                                        key={grn.id}
                                                        type="button"
                                                        className={`cc-grn-result ${isSelected ? "selected" : ""}`}
                                                        role="option"
                                                        aria-selected={isSelected}
                                                        disabled={saving}
                                                        onClick={() => {
                                                            const id = String(grn.id);
                                                            updateForm("grnId", id);
                                                            setGrnSearch(
                                                                `${grn.grn_no} — ${grn.customer_name}`
                                                            );
                                                            loadSelectedGrn(id);
                                                        }}
                                                    >
                                                        <strong>
                                                            {grn.customer_name || "Unknown customer"}
                                                        </strong>
                                                        <span>
                                                            {grn.grn_no} · {formatDate(grn.grn_date)}
                                                        </span>
                                                    </button>
                                                );
                                            })
                                        ) : (
                                            <p className="cc-grn-no-results" role="status">
                                                No matching customer found.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="cc-field">
                                <label>Bill Date *</label>

                                <input
                                    type="date"
                                    required
                                    min={
                                        selectedGrn
                                            ? String(selectedGrn.grn_date).slice(0, 10)
                                            : undefined
                                    }
                                    value={form.billDate}
                                    onChange={(event) =>
                                        updateForm(
                                            "billDate",
                                            event.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="cc-field">
                                <label>Transport Bill No.</label>

                                <input
                                    value={form.transportBillNo}
                                    onChange={(event) =>
                                        updateForm(
                                            "transportBillNo",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Enter transport bill number"
                                />
                            </div>
                        </div>

                        {selectedGrn && (
                            <div className="cc-grn-preview">
                                <div>
                                    <span>Customer</span>
                                    <strong>
                                        {selectedGrn.customer_name || "—"}
                                    </strong>
                                </div>

                                <div>
                                    <span>GRN Date</span>
                                    <strong>
                                        {formatDate(selectedGrn.grn_date)}
                                    </strong>
                                </div>

                                <div>
                                    <span>Lots</span>
                                    <strong>
                                        {selectedGrn.items?.length || 0}
                                    </strong>
                                </div>

                                <div>
                                    <span>Total Bags</span>
                                    <strong>
                                        {(selectedGrn.items || []).reduce(
                                            (sum, item) =>
                                                sum + number(item.bags),
                                            0
                                        ).toLocaleString("en-IN")}
                                    </strong>
                                </div>

                                <div>
                                    <span>Total Weight</span>
                                    <strong>
                                        {(selectedGrn.items || []).reduce(
                                            (sum, item) =>
                                                sum + number(item.weight_kg),
                                            0
                                        ).toLocaleString("en-IN")} kg
                                    </strong>
                                </div>
                            </div>
                        )}

                        {selectedGrn && (
                            <div className="cc-lot-preview">
                                <h3>
                                    <Package size={18} />
                                    GRN Lot Details
                                </h3>

                                <div className="cc-table-wrap">
                                    <table className="cc-table">
                                        <thead>
                                            <tr>
                                                <th>Chamber</th>
                                                <th>Lot No.</th>
                                                <th>Product Mark</th>
                                                <th>Location</th>
                                                <th>Bags</th>
                                                <th>Weight (KG)</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {(selectedGrn.items || []).map(
                                                (item, index) => (
                                                    <tr
                                                        key={
                                                            item.grn_item_id ||
                                                            index
                                                        }
                                                    >
                                                        <td>
                                                            {item.chamber_no || "—"}
                                                        </td>
                                                        <td>
                                                            {item.lot_no || "—"}
                                                        </td>
                                                        <td>
                                                            {item.product_mark || "—"}
                                                        </td>
                                                        <td>
                                                            {item.location || "—"}
                                                        </td>
                                                        <td>
                                                            {number(item.bags).toLocaleString("en-IN")}
                                                        </td>
                                                        <td>
                                                            {number(item.weight_kg).toLocaleString("en-IN")}
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="cc-section-heading">
                            <Truck size={19} />
                            <h3>Transport and Interest</h3>
                        </div>

                        <div className="cc-form-grid">
                            <div className="cc-field">
                                <label>Transport Bill Amount (₹) *</label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    required
                                    value={form.transportBillAmount}
                                    onChange={(event) =>
                                        updateForm(
                                            "transportBillAmount",
                                            event.target.value
                                        )
                                    }
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="cc-field">
                                <label>Monthly Interest Rate (%)</label>

                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.001"
                                    required
                                    value={form.interestRateMonthly}
                                    onChange={(event) =>
                                        updateForm(
                                            "interestRateMonthly",
                                            event.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="cc-field">
                                <label>Transport Interest (₹)</label>
                                <input
                                    value={calculation.interest.toFixed(2)}
                                    readOnly
                                />
                            </div>
                        </div>

                        <div className="cc-section-heading">
                            <Wallet size={19} />
                            <h3>Other Charges</h3>
                        </div>

                        <div className="cc-form-grid">
                            <div className="cc-field">
                                <label>Hamali / Labour (₹)</label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.hamaliAmount}
                                    onChange={(event) =>
                                        updateForm(
                                            "hamaliAmount",
                                            event.target.value
                                        )
                                    }
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="cc-field">
                                <label>Storage Rent (₹)</label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.storageRentAmount}
                                    onChange={(event) =>
                                        updateForm(
                                            "storageRentAmount",
                                            event.target.value
                                        )
                                    }
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="cc-field">
                                <label>Other Charges (₹)</label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.otherCharges}
                                    onChange={(event) =>
                                        updateForm(
                                            "otherCharges",
                                            event.target.value
                                        )
                                    }
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="cc-field">
                                <label>Discount (₹)</label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.discountAmount}
                                    onChange={(event) =>
                                        updateForm(
                                            "discountAmount",
                                            event.target.value
                                        )
                                    }
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        <div className="cc-field cc-notes-field">
                            <label>Notes</label>

                            <textarea
                                rows="2"
                                value={form.notes}
                                onChange={(event) =>
                                    updateForm("notes", event.target.value)
                                }
                                placeholder="Optional bill notes"
                            />
                        </div>

                        <div className="cc-calculation-card">
                            <h3>Bill Calculation</h3>

                            <div>
                                <span>Transport Bill</span>
                                <strong>{currency(calculation.transport)}</strong>
                            </div>

                            <div>
                                <span>Transport Interest</span>
                                <strong>{currency(calculation.interest)}</strong>
                            </div>

                            <div>
                                <span>Hamali / Labour</span>
                                <strong>{currency(calculation.hamali)}</strong>
                            </div>

                            <div>
                                <span>Storage Rent</span>
                                <strong>{currency(calculation.rent)}</strong>
                            </div>

                            <div>
                                <span>Other Charges</span>
                                <strong>{currency(calculation.other)}</strong>
                            </div>

                            <div>
                                <span>Discount</span>
                                <strong>− {currency(calculation.discount)}</strong>
                            </div>

                            <div className="cc-grand-total">
                                <span>Final Receivable Amount</span>
                                <strong>{currency(calculation.total)}</strong>
                            </div>
                        </div>

                        <div className="cc-form-actions">
                            <button
                                type="button"
                                className="cc-btn cc-btn-secondary"
                                onClick={closeCreateForm}
                                disabled={saving}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="cc-btn cc-btn-primary"
                                disabled={saving || !selectedGrn}
                            >
                                <Save size={17} />

                                {saving
                                    ? "Saving..."
                                    : editingBillId
                                        ? "Save Changes"
                                        : "Save Customer Bill"}
                            </button>
                        </div>
                    </form>
                </section>
            )}

            <section className="cc-panel">
                <div className="cc-panel-header cc-list-header">
                    <div>
                        <h2>
                            <FileText size={20} />
                            Customer Bills Register
                        </h2>

                        <p>{filteredBills.length} record(s) found</p>
                    </div>
                </div>

                <div className="cc-filters">
                    <div className="cc-search">
                        <Search size={19} />

                        <input
                            type="search"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search bill no., GRN, customer, transport bill..."
                        />
                        <button
                            type="button"
                            className={`register-voice-button ${registerListening ? "is-listening" : ""}`}
                            onClick={startRegisterVoiceSearch}
                            title="Search bills by Marathi voice"
                            aria-label="Search bills by Marathi voice"
                        >
                            <Mic size={18} />
                        </button>
                    </div>

                    <select
                        value={statusFilter}
                        onChange={(event) =>
                            setStatusFilter(event.target.value)
                        }
                        aria-label="Filter by payment status"
                    >
                        <option value="ALL">All Status</option>
                        <option value="Unpaid">Unpaid</option>
                        <option value="Partial">Partial</option>
                        <option value="Paid">Paid</option>
                    </select>
                </div>

                                {loading ? (
                    <div className="cc-empty">
                        <RefreshCw size={25} className="cc-spin" />
                        <p>Loading customer bills...</p>
                    </div>
                ) : filteredBills.length === 0 ? (
                    <div className="cc-empty">
                        <FileText size={35} />
                        <h3>No customer bills found</h3>
                        <p>
                            Create a bill from a Goods Received record to get started.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="cc-table-wrap cc-desktop-table">
                            <table className="cc-table">
                                <thead>
                                    <tr>
                                        <th>Bill No. / Date</th>
                                        <th>Customer</th>
                                        <th>GRN No. / Date</th>
                                        <th>Transport</th>
                                        <th>Interest</th>
                                        <th>Hamali</th>
                                        <th>Storage Rent</th>
                                        <th>Total</th>
                                        <th>Paid</th>
                                        <th>Balance</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredBills.map((bill) => (
                                        <tr key={bill.id}>
                                            <td>
                                                <strong>{bill.bill_no}</strong>
                                                <small>{formatDate(bill.bill_date)}</small>
                                            </td>

                                            <td>{bill.customer_name}</td>

                                            <td>
                                                {bill.grn_no}
                                                <small>{formatDate(bill.grn_date)}</small>
                                            </td>

                                            <td>{currency(bill.transport_bill_amount)}</td>
                                            <td>{currency(bill.transport_interest_amount)}</td>
                                            <td>{currency(bill.hamali_amount)}</td>
                                            <td>{currency(bill.storage_rent_amount)}</td>

                                            <td>
                                                <strong>{currency(bill.total_amount)}</strong>
                                            </td>

                                            <td>{currency(bill.amount_paid)}</td>

                                            <td>
                                                <strong>{currency(bill.balance_due)}</strong>
                                            </td>

                                            <td>
                                                <span
                                                    className={`cc-status cc-status-${String(
                                                        bill.payment_status || "Unpaid"
                                                    ).toLowerCase()}`}
                                                >
                                                    {bill.payment_status || "Unpaid"}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="cc-row-actions">
                                                    <button
                                                        type="button"
                                                        className="cc-icon-btn"
                                                        title="View bill"
                                                        onClick={() => openBill(bill.id)}
                                                    >
                                                        <Eye size={17} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="cc-icon-btn cc-print-btn"
                                                        title="Print / Save PDF"
                                                        onClick={() => printBill(bill.id)}
                                                    >
                                                        <Printer size={17} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="cc-icon-btn"
                                                        title="Edit bill"
                                                        onClick={() => editBill(bill.id)}
                                                    >
                                                        <Pencil size={17} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="cc-icon-btn cc-delete-btn"
                                                        title="Delete bill"
                                                        onClick={() => deleteBill(bill)}
                                                    >
                                                        <Trash2 size={17} />
                                                    </button>

                                                    {number(bill.balance_due) > 0 && (
                                                        <button
                                                            type="button"
                                                            className="cc-icon-btn cc-payment-btn"
                                                            title="Record payment"
                                                            onClick={() => {
                                                                setPaymentBill(bill);
                                                                setPaymentForm(emptyPayment());
                                                                setError("");
                                                                setSuccess("");
                                                            }}
                                                        >
                                                            <CreditCard size={17} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="cc-mobile-list">
                            {filteredBills.map((bill) => (
                                <article
                                    className="cc-mobile-bill"
                                    key={bill.id}
                                >
                                    <div className="cc-mobile-bill-header">
                                        <div>
                                            <strong>{bill.bill_no}</strong>
                                            <span>{formatDate(bill.bill_date)}</span>
                                        </div>

                                        <span
                                            className={`cc-status cc-status-${String(
                                                bill.payment_status || "Unpaid"
                                            ).toLowerCase()}`}
                                        >
                                            {bill.payment_status || "Unpaid"}
                                        </span>
                                    </div>

                                    <p className="cc-mobile-customer">
                                        {bill.customer_name}
                                    </p>

                                    <p className="cc-muted">
                                        GRN: {bill.grn_no} · {formatDate(bill.grn_date)}
                                    </p>

                                    <div className="cc-mobile-bill-grid">
                                        <div>
                                            <span>Total</span>
                                            <strong>{currency(bill.total_amount)}</strong>
                                        </div>

                                        <div>
                                            <span>Paid</span>
                                            <strong>{currency(bill.amount_paid)}</strong>
                                        </div>

                                        <div>
                                            <span>Balance</span>
                                            <strong>{currency(bill.balance_due)}</strong>
                                        </div>

                                        <div>
                                            <span>Transport Interest</span>
                                            <strong>
                                                {currency(bill.transport_interest_amount)}
                                            </strong>
                                        </div>
                                    </div>

                                    <div className="cc-mobile-actions">
                                        <button
                                            className="cc-btn cc-btn-secondary"
                                            type="button"
                                            onClick={() => openBill(bill.id)}
                                        >
                                            <Eye size={16} />
                                            View
                                        </button>

                                        <button
                                            className="cc-btn cc-btn-secondary"
                                            type="button"
                                            onClick={() => printBill(bill.id)}
                                        >
                                            <Printer size={16} />
                                            Print
                                        </button>

                                        <button
                                            className="cc-btn cc-btn-secondary"
                                            type="button"
                                            onClick={() => editBill(bill.id)}
                                        >
                                            <Pencil size={16} />
                                            Edit
                                        </button>

                                        <button
                                            className="cc-btn cc-btn-secondary cc-delete-btn"
                                            type="button"
                                            onClick={() => deleteBill(bill)}
                                        >
                                            <Trash2 size={16} />
                                            Delete
                                        </button>

                                        {number(bill.balance_due) > 0 && (
                                            <button
                                                className="cc-btn cc-btn-primary"
                                                type="button"
                                                onClick={() => {
                                                    setPaymentBill(bill);
                                                    setPaymentForm(emptyPayment());
                                                    setError("");
                                                    setSuccess("");
                                                }}
                                            >
                                                <CreditCard size={16} />
                                                Payment
                                            </button>
                                        )}
                                    </div>
                                </article>
                            ))}
                        </div>
                    </>
                )}
            </section>

            {/* PAYMENT MODAL */}
            {paymentBill && (
                <div
                    className="cc-modal-overlay"
                    onMouseDown={(event) => {
                        if (
                            event.target === event.currentTarget &&
                            !savingPayment
                        ) {
                            setPaymentBill(null);
                        }
                    }}
                >
                    <section
                        className="cc-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="cc-payment-title"
                    >
                        <div className="cc-modal-header">
                            <div>
                                <h2 id="cc-payment-title">Record Payment</h2>
                                <p>
                                    {paymentBill.bill_no} — {paymentBill.customer_name}
                                </p>
                            </div>

                            <button
                                type="button"
                                className="cc-icon-btn"
                                onClick={() => setPaymentBill(null)}
                                disabled={savingPayment}
                                aria-label="Close payment form"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="cc-payment-balance">
                            <span>Outstanding Balance</span>
                            <strong>{currency(paymentBill.balance_due)}</strong>
                        </div>

                        <form onSubmit={submitPayment}>
                            <div className="cc-field">
                                <label>Payment Date *</label>
                                <input
                                    type="date"
                                    required
                                    value={paymentForm.paymentDate}
                                    onChange={(event) =>
                                        updatePaymentForm(
                                            "paymentDate",
                                            event.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="cc-field">
                                <label>Payment Method *</label>
                                <select
                                    required
                                    value={paymentForm.paymentMethod}
                                    onChange={(event) =>
                                        updatePaymentForm(
                                            "paymentMethod",
                                            event.target.value
                                        )
                                    }
                                >
                                    {[
                                        "Cash",
                                        "UPI",
                                        "Bank Transfer",
                                        "NEFT",
                                        "RTGS",
                                        "Cheque",
                                        "Other"
                                    ].map((method) => (
                                        <option key={method} value={method}>
                                            {method}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="cc-field">
                                <label>Amount Received (₹) *</label>
                                <input
                                    type="number"
                                    min="0.01"
                                    max={number(paymentBill.balance_due)}
                                    step="0.01"
                                    required
                                    value={paymentForm.amount}
                                    onChange={(event) =>
                                        updatePaymentForm(
                                            "amount",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Enter payment amount"
                                />
                            </div>

                            <div className="cc-field">
                                <label>Reference Number</label>
                                <input
                                    value={paymentForm.referenceNo}
                                    onChange={(event) =>
                                        updatePaymentForm(
                                            "referenceNo",
                                            event.target.value
                                        )
                                    }
                                    placeholder="UPI / bank / cheque reference"
                                />
                            </div>

                            <div className="cc-field">
                                <label>Notes</label>
                                <textarea
                                    rows="2"
                                    value={paymentForm.notes}
                                    onChange={(event) =>
                                        updatePaymentForm(
                                            "notes",
                                            event.target.value
                                        )
                                    }
                                    placeholder="Optional payment notes"
                                />
                            </div>

                            <div className="cc-form-actions">
                                <button
                                    type="button"
                                    className="cc-btn cc-btn-secondary"
                                    onClick={() => setPaymentBill(null)}
                                    disabled={savingPayment}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="cc-btn cc-btn-primary"
                                    disabled={savingPayment}
                                >
                                    <Save size={17} />
                                    {savingPayment
                                        ? "Saving..."
                                        : "Save Payment"}
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            )}

            {/* BILL DETAILS MODAL */}
            {viewBill && (
                <div
                    className="cc-modal-overlay"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            setViewBill(null);
                        }
                    }}
                >
                    <section
                        className="cc-modal cc-view-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="cc-view-title"
                    >
                        <div className="cc-modal-header">
                            <div>
                                <h2 id="cc-view-title">Bill Details</h2>
                                <p>
                                    {viewBill.bill_no} · {viewBill.customer_name}
                                </p>
                            </div>

                            <button
                                type="button"
                                className="cc-icon-btn"
                                onClick={() => setViewBill(null)}
                                aria-label="Close bill details"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="cc-view-meta">
                            <div>
                                <span>Bill Date</span>
                                <strong>{formatDate(viewBill.bill_date)}</strong>
                            </div>

                            <div>
                                <span>GRN No.</span>
                                <strong>{viewBill.grn_no}</strong>
                            </div>

                            <div>
                                <span>Goods Received</span>
                                <strong>{formatDate(viewBill.grn_date)}</strong>
                            </div>

                            <div>
                                <span>Transport Bill No.</span>
                                <strong>{viewBill.transport_bill_no || "—"}</strong>
                            </div>
                        </div>

                        <h3 className="cc-view-subtitle">GRN Lot Details</h3>

                        <div className="cc-table-wrap">
                            <table className="cc-table">
                                <thead>
                                    <tr>
                                        <th>Chamber</th>
                                        <th>Lot</th>
                                        <th>Product Mark</th>
                                        <th>Bags</th>
                                        <th>Weight (KG)</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {(viewBill.items || []).map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>{item.chamber_no || "—"}</td>
                                            <td>{item.lot_no || "—"}</td>
                                            <td>{item.product_mark || "—"}</td>
                                            <td>
                                                {number(item.bags).toLocaleString("en-IN")}
                                            </td>
                                            <td>
                                                {number(item.weight_kg).toLocaleString("en-IN")}
                                            </td>
                                        </tr>
                                    ))}

                                    {(viewBill.items || []).length === 0 && (
                                        <tr>
                                            <td colSpan="5">
                                                No lot details available.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <h3 className="cc-view-subtitle">Charges</h3>

                        <div className="cc-view-totals">
                            <div>
                                <span>Transport Bill</span>
                                <strong>{currency(viewBill.transport_bill_amount)}</strong>
                            </div>

                            <div>
                                <span>
                                    Transport Interest (
                                    {number(viewBill.interest_rate_monthly)}% monthly /
                                    {number(viewBill.interest_days)} days)
                                </span>
                                <strong>
                                    {currency(viewBill.transport_interest_amount)}
                                </strong>
                            </div>

                            <div>
                                <span>Hamali</span>
                                <strong>{currency(viewBill.hamali_amount)}</strong>
                            </div>

                            <div>
                                <span>Storage Rent</span>
                                <strong>{currency(viewBill.storage_rent_amount)}</strong>
                            </div>

                            <div>
                                <span>Other Charges</span>
                                <strong>{currency(viewBill.other_charges)}</strong>
                            </div>

                            <div>
                                <span>Discount</span>
                                <strong>
                                    − {currency(viewBill.discount_amount)}
                                </strong>
                            </div>

                            <div className="cc-view-total">
                                <span>Total Receivable</span>
                                <strong>{currency(viewBill.total_amount)}</strong>
                            </div>

                            <div>
                                <span>Amount Paid</span>
                                <strong>{currency(viewBill.amount_paid)}</strong>
                            </div>

                            <div className="cc-view-balance">
                                <span>Balance Due</span>
                                <strong>{currency(viewBill.balance_due)}</strong>
                            </div>
                        </div>

                        {viewBill.notes && (
                            <div className="cc-field">
                                <label>Notes</label>
                                <p>{viewBill.notes}</p>
                            </div>
                        )}

                        <h3 className="cc-view-subtitle">Payment History</h3>

                        <div className="cc-table-wrap">
                            <table className="cc-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Method</th>
                                        <th>Reference</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {(viewBill.payments || []).length ? (
                                        viewBill.payments.map((payment, index) => (
                                            <tr key={payment.id || index}>
                                                <td>
                                                    {formatDate(payment.payment_date)}
                                                </td>
                                                <td>
                                                    {payment.payment_method || "—"}
                                                </td>
                                                <td>
                                                    {payment.reference_no || "—"}
                                                </td>
                                                <td>{currency(payment.amount)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4">
                                                No payments recorded.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="cc-form-actions">
                            <button
                                type="button"
                                className="cc-btn cc-btn-secondary"
                                onClick={() => printBill(viewBill.id)}
                            >
                                <Printer size={17} />
                                Print / Save PDF
                            </button>

                            <button
                                type="button"
                                className="cc-btn cc-btn-secondary"
                                onClick={() => editBill(viewBill.id)}
                            >
                                <Pencil size={17} />
                                Edit Bill
                            </button>

                            <button
                                type="button"
                                className="cc-btn cc-btn-primary"
                                onClick={() => setViewBill(null)}
                            >
                                Close
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}

export default CustomerCharges;
